import React, { useState } from 'react';
import {
  Box,
  Button,
  Grid,
  TextField,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Typography,
  FormControlLabel,
  Switch,
  Slider,
  Tooltip,
  IconButton
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import { qbColors, qbFonts } from './queryBuilderTheme';
import { useFormat } from '../context/FormatContext';


// Info tooltip component
const InfoTooltip = ({ tooltip }) => (
  <Tooltip title={tooltip} placement="top">
    <IconButton size="small" sx={{ p: 0.5 }}>
      <InfoIcon fontSize="small" color="action" />
    </IconButton>
  </Tooltip>
);

// Coverage warning component
const CoverageWarning = ({ coverage, columnName }) => {
  if (coverage >= 80) return null;
  
  const color = coverage < 50 ? qbColors.gold : qbColors.blue;
  
  return (
    <Tooltip title={`${columnName} data is available for ${coverage}% of deliveries. Filtering may reduce results significantly.`}>
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', ml: 0.5 }}>
        <WarningIcon sx={{ fontSize: 16, color }} />
      </Box>
    </Tooltip>
  );
};

const ORDINALS = ['1st', '2nd', '3rd', '4th'];
const ordinalInnings = (n) => `${ORDINALS[n - 1] || `${n}th`} Innings`;


const THRESHOLD_METRICS = [
  ['average', 'Average'],
  ['strike_rate', 'Strike rate'],
  ['balls_per_dismissal', 'Balls per dismissal'],
  ['dot_percentage', 'Dot %'],
  ['boundary_percentage', 'Boundary %'],
  ['control_percentage', 'Control %'],
  ['impact_per_100', 'Impact / 100 balls'],
  ['impact', 'Impact'],
  ['wpa', 'WPA'],
  ['raa_per_100', 'RAA / 100 balls'],
];
const THRESHOLD_OPS = { gte: '≥', lte: '≤', gt: '>', lt: '<' };

const thresholdLabel = (raw) => {
  const [metric, op, value] = String(raw).split(':');
  const name = (THRESHOLD_METRICS.find(([k]) => k === metric) || [metric, metric.replace(/_/g, ' ')])[1];
  return `${name} ${THRESHOLD_OPS[op] || op} ${value}`;
};

/** Grouped-result thresholds on computed metrics, stored as 'metric:op:value' strings. */
const MetricThresholds = ({ value, onChange }) => {
  const [metric, setMetric] = useState('average');
  const [op, setOp] = useState('gte');
  const [amount, setAmount] = useState('');
  const add = () => {
    const n = Number(amount);
    if (amount === '' || Number.isNaN(n)) return;
    const key = `${metric}:${op}:${n}`;
    onChange([...value.filter((v) => !v.startsWith(`${metric}:${op}:`)), key]);
    setAmount('');
  };
  return (
    <Box>
      <Typography variant="body2" gutterBottom sx={{ color: qbColors.textMed }}>
        Metric filters (grouped results)
      </Typography>
      {value.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
          {value.map((v) => (
            <Chip key={v} label={thresholdLabel(v)} size="small" onDelete={() => onChange(value.filter((x) => x !== v))} />
          ))}
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField select size="small" value={metric} onChange={(e) => setMetric(e.target.value)} sx={{ minWidth: 150, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}>
          {THRESHOLD_METRICS.map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
        </TextField>
        <TextField select size="small" value={op} onChange={(e) => setOp(e.target.value)} sx={{ width: 76 }}>
          {Object.entries(THRESHOLD_OPS).map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
        </TextField>
        <TextField
          size="small"
          type="number"
          placeholder="Value"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          sx={{ width: 100 }}
        />
        <Button size="small" variant="outlined" onClick={add} disabled={amount === ''} sx={{ minHeight: 40 }}>
          Add
        </Button>
      </Box>
    </Box>
  );
};

const QueryFilters = ({ filters, setFilters, groupBy, setGroupBy, availableColumns, isMobile }) => {
  // On phones the two specialist groups fold away (about 1.5 screens of fields) unless one of
  // their filters is already set -- e.g. from a shared link or an NL query.
  const SECTION_KEYS = {
    context: ['match_outcome', 'is_chase', 'chase_outcome', 'toss_decision', 'bat_hand', 'bowl_style', 'bowl_kind'],
    delivery: ['line', 'length', 'shot', 'control', 'wagon_zone'],
  };
  const [openSections, setOpenSections] = useState({ context: false, delivery: false });
  const sectionHasValues = (section) => SECTION_KEYS[section].some((key) => {
    const value = filters[key];
    return Array.isArray(value) ? value.length > 0 : value !== null && value !== undefined && value !== '';
  });
  const sectionOpen = (section) => !isMobile || openSections[section] || sectionHasValues(section);
  const sectionStyle = (section) => (sectionOpen(section) ? undefined : { display: 'none' });
  const toggleSection = (section) => setOpenSections((prev) => ({ ...prev, [section]: !sectionOpen(section) }));
  // All dropdown data now comes from availableColumns (fetched from delivery_details)
  const { active } = useFormat();
  const internationalOnly = active?.format === 'ODI' || active?.format === 'TEST';

  // Limits come from the selected format, not from T20 constants: an over 40 filter is normal
  // for an ODI and meaningless for a T20, and Tests have four innings rather than two.
  const overMax = active?.over_max ?? 19;
  const inningsOptions = Array.from(
    { length: active?.innings_count ?? 2 },
    (_unused, index) => index + 1,
  );
  
  const handleFilterChange = (key, value) => {
    setFilters(prev => {
      const next = {
        ...prev,
        [key]: value
      };

      // Keep chase-outcome filters consistent with API rules.
      if (key === 'is_chase' && value === false) {
        next.chase_outcome = [];
      }
      if (key === 'innings' && value !== 2) {
        next.chase_outcome = [];
      }

      return next;
    });
  };
  
  const handleGroupByChange = (event, newValue) => {
    setGroupBy(newValue || []);
  };
  
  if (!availableColumns) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography>Loading filter options...</Typography>
      </Box>
    );
  }

  const chaseOutcomeDisabled = filters.is_chase === false || filters.innings === 1;

  const chipSx = {
    color: qbColors.textMed,
    borderColor: qbColors.borderStrong,
    bgcolor: 'rgba(255,255,255,0.035)',
    '& .MuiChip-deleteIcon': { color: qbColors.textFaint },
  };

  return (
    <Box>
      <Grid container spacing={isMobile ? 1.5 : 2.2}>
        {/* Row 1: Date Range & Venue */}
        <Grid item xs={12} sm={4} md={3}>
          <TextField
            label="Start Date"
            type="date"
            value={filters.start_date || ''}
            onChange={(e) => handleFilterChange('start_date', e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            fullWidth
          />
        </Grid>
        
        <Grid item xs={12} sm={4} md={3}>
          <TextField
            label="End Date"
            type="date"
            value={filters.end_date || ''}
            onChange={(e) => handleFilterChange('end_date', e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            fullWidth
          />
        </Grid>
        
        <Grid item xs={12} sm={4} md={3}>
          <Autocomplete
            value={filters.venue}
            onChange={(e, value) => handleFilterChange('venue', value)}
            options={availableColumns?.venues || []}
            renderInput={(params) => (
              <TextField {...params} label="Venue" size="small" />
            )}
          />
        </Grid>
        
        <Grid item xs={12} sm={4} md={3}>
          <Autocomplete
            multiple
            value={filters.leagues}
            onChange={(e, value) => handleFilterChange('leagues', value)}
            options={availableColumns?.competitions || []}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label={internationalOnly ? 'Competitions' : 'Leagues'} size="small" />
            )}
          />
        </Grid>

        {/* Row 2: Teams */}
        <Grid item xs={12} sm={4} md={3}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Autocomplete
              multiple
              value={filters.batting_teams || []}
              onChange={(e, value) => handleFilterChange('batting_teams', value)}
              options={availableColumns?.batting_teams || []}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="outlined" label={option} size="small" sx={chipSx} {...getTagProps({ index })} />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label="Batting Teams" size="small" />
              )}
              sx={{ flexGrow: 1 }}
            />
            <InfoTooltip tooltip="Teams when batting" />
          </Box>
        </Grid>
        
        <Grid item xs={12} sm={4} md={3}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Autocomplete
              multiple
              value={filters.bowling_teams || []}
              onChange={(e, value) => handleFilterChange('bowling_teams', value)}
              options={availableColumns?.bowling_teams || []}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="outlined" label={option} size="small" sx={chipSx} {...getTagProps({ index })} />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label="Bowling Teams" size="small" />
              )}
              sx={{ flexGrow: 1 }}
            />
            <InfoTooltip tooltip="Teams when bowling" />
          </Box>
        </Grid>
        
        {/* ODIs are all between national sides: nothing to include, and no top-teams cut. */}
        {!internationalOnly && (
          <Grid item xs={12} sm={4} md={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={filters.include_international}
                  onChange={(e) => handleFilterChange('include_international', e.target.checked)}
                  size="small"
                />
              }
              label={active?.format === 'T20' ? 'Include T20I' : 'Include internationals'}
            />
          </Grid>
        )}
        
        {/* Row 3: Players */}
        <Grid item xs={12} sm={6}>
          <Autocomplete
            multiple
            value={filters.batters || []}
            onChange={(e, value) => handleFilterChange('batters', value)}
            options={availableColumns?.batters || []}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Batters"
                size="small"
                helperText={(groupBy || []).includes('partnership') && (filters.batters || []).length
                  ? "Grouped by partnership: counts every stand involving these players, both batters' balls"
                  : undefined}
              />
            )}
          />
        </Grid>

        {(groupBy || []).includes('partnership') && (
          <Grid item xs={12} sm={6}>
            {/* Every ball either batter faced, so a stand's row is the whole stand
                (a batters filter alone used to count only the named batter's balls). */}
            <Autocomplete
              multiple
              value={filters.partnership_players || []}
              onChange={(e, value) => handleFilterChange('partnership_players', value)}
              options={availableColumns?.batters || []}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label="Partnerships involving" size="small" />
              )}
            />
          </Grid>
        )}
        
        <Grid item xs={12} sm={6}>
          <Autocomplete
            multiple
            value={filters.bowlers || []}
            onChange={(e, value) => handleFilterChange('bowlers', value)}
            options={availableColumns?.bowlers || []}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="Bowlers" size="small" />
            )}
          />
        </Grid>
        
        {/* Row 4: Match Context */}
        <Grid item xs={12} sm={4} md={3}>
          <FormControl size="small" fullWidth>
            <InputLabel>Innings</InputLabel>
            <Select
              value={filters.innings || ''}
              onChange={(e) => handleFilterChange('innings', e.target.value || null)}
              label="Innings"
            >
              <MenuItem value="">All</MenuItem>
              {inningsOptions.map((n) => (
                <MenuItem key={n} value={n}>{ordinalInnings(n)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={4} md={3}>
          <TextField
            label={`Over Min (0-${overMax})`}
            type="number"
            value={filters.over_min ?? ''}
            onChange={(e) => handleFilterChange('over_min', e.target.value ? parseInt(e.target.value) : null)}
            inputProps={{ min: 0, max: overMax }}
            size="small"
            fullWidth
          />
        </Grid>
        
        <Grid item xs={12} sm={4} md={3}>
          <TextField
            label={`Over Max (0-${overMax})`}
            type="number"
            value={filters.over_max ?? ''}
            onChange={(e) => handleFilterChange('over_max', e.target.value ? parseInt(e.target.value) : null)}
            inputProps={{ min: 0, max: overMax }}
            size="small"
            fullWidth
          />
        </Grid>

        {/* Row 5: Advanced Match Context */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle2"
            component={isMobile ? 'button' : 'p'}
            onClick={isMobile ? () => toggleSection('context') : undefined}
            sx={{ background: 'none', border: 0, p: 0, cursor: isMobile ? 'pointer' : 'default', minHeight: isMobile ? 36 : undefined,  mb: 1, mt: 1, color: qbColors.textLo, fontFamily: qbFonts.mono, fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
            Advanced Match Context{isMobile ? (sectionOpen('context') ? ' ▴' : ' ▾') : ''}
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6} md={3} style={sectionStyle('context')}>
          <Autocomplete
            multiple
            value={filters.match_outcome || []}
            onChange={(e, value) => handleFilterChange('match_outcome', value)}
            options={availableColumns?.match_outcome_options || ['win', 'loss', 'tie', 'no_result']}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="Match Outcome" size="small" />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3} style={sectionStyle('context')}>
          <FormControl size="small" fullWidth>
            <InputLabel>Is Chase</InputLabel>
            <Select
              value={filters.is_chase === null || filters.is_chase === undefined ? '' : String(filters.is_chase)}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') {
                  handleFilterChange('is_chase', null);
                } else {
                  handleFilterChange('is_chase', raw === 'true');
                }
              }}
              label="Is Chase"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Yes (2nd innings)</MenuItem>
              <MenuItem value="false">No (1st innings)</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={3} style={sectionStyle('context')}>
          <Autocomplete
            multiple
            disabled={chaseOutcomeDisabled}
            value={filters.chase_outcome || []}
            onChange={(e, value) => handleFilterChange('chase_outcome', value)}
            options={availableColumns?.chase_outcome_options || ['win', 'loss', 'tie', 'no_result']}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Chase Outcome"
                size="small"
                helperText={chaseOutcomeDisabled ? 'Requires chase innings' : ''}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3} style={sectionStyle('context')}>
          <Autocomplete
            multiple
            value={filters.toss_decision || []}
            onChange={(e, value) => handleFilterChange('toss_decision', value)}
            options={availableColumns?.toss_decision_options || ['bat', 'field']}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="Toss Decision" size="small" />
            )}
          />
        </Grid>
        
        {/* Row 6: Batter/Bowler Attributes */}
        <Grid item xs={12} sm={4} md={3} style={sectionStyle('context')}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Bat Hand</InputLabel>
              <Select
                value={filters.bat_hand || ''}
                onChange={(e) => handleFilterChange('bat_hand', e.target.value || null)}
                label="Bat Hand"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="RHB">Right Hand (RHB)</MenuItem>
                <MenuItem value="LHB">Left Hand (LHB)</MenuItem>
              </Select>
            </FormControl>
            <CoverageWarning coverage={availableColumns?.bat_hand_coverage} columnName="Bat hand" />
          </Box>
        </Grid>
        
        <Grid item xs={12} sm={4} md={3} style={sectionStyle('context')}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Autocomplete
              multiple
              value={filters.bowl_style || []}
              onChange={(e, value) => handleFilterChange('bowl_style', value)}
              options={availableColumns?.bowl_style_options || []}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label="Bowl Style" size="small" />
              )}
              sx={{ flexGrow: 1 }}
            />
            <InfoTooltip tooltip="RF=Right Fast, RM=Right Medium, SLA=Slow Left Arm, OB=Off Break, LB=Leg Break" />
          </Box>
        </Grid>
        
        <Grid item xs={12} sm={4} md={3} style={sectionStyle('context')}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Autocomplete
              multiple
              value={filters.bowl_kind || []}
              onChange={(e, value) => handleFilterChange('bowl_kind', value)}
              options={availableColumns?.bowl_kind_options || []}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label="Bowl Kind" size="small" />
              )}
              sx={{ flexGrow: 1 }}
            />
            <InfoTooltip tooltip="Pace bowler, Spin bowler, or Mixture/Unknown" />
          </Box>
        </Grid>
        
        {/* Row 7: Delivery Details - NEW */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle2"
            component={isMobile ? 'button' : 'p'}
            onClick={isMobile ? () => toggleSection('delivery') : undefined}
            sx={{ background: 'none', border: 0, p: 0, cursor: isMobile ? 'pointer' : 'default', minHeight: isMobile ? 36 : undefined,  mb: 1, mt: 1, color: qbColors.gold, fontFamily: qbFonts.mono, fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
            Delivery Analysis Filters{isMobile ? (sectionOpen('delivery') ? ' ▴' : ' ▾') : ''}
          </Typography>
        </Grid>
        
        <Grid item xs={12} sm={4} md={3} style={sectionStyle('delivery')}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Autocomplete
              multiple
              value={filters.line || []}
              onChange={(e, value) => handleFilterChange('line', value)}
              options={availableColumns?.line_options || []}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label="Line" size="small" />
              )}
              sx={{ flexGrow: 1 }}
            />
            <CoverageWarning coverage={availableColumns?.line_coverage} columnName="Line" />
          </Box>
        </Grid>
        
        <Grid item xs={12} sm={4} md={3} style={sectionStyle('delivery')}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Autocomplete
              multiple
              value={filters.length || []}
              onChange={(e, value) => handleFilterChange('length', value)}
              options={availableColumns?.length_options || []}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label="Length" size="small" />
              )}
              sx={{ flexGrow: 1 }}
            />
            <CoverageWarning coverage={availableColumns?.length_coverage} columnName="Length" />
          </Box>
        </Grid>
        
        <Grid item xs={12} sm={4} md={3} style={sectionStyle('delivery')}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Autocomplete
              multiple
              value={filters.shot || []}
              onChange={(e, value) => handleFilterChange('shot', value)}
              options={availableColumns?.shot_options || []}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label="Shot Type" size="small" />
              )}
              sx={{ flexGrow: 1 }}
            />
            <CoverageWarning coverage={availableColumns?.shot_coverage} columnName="Shot" />
          </Box>
        </Grid>

        <Grid item xs={12} sm={4} md={3} style={sectionStyle('delivery')}>
          {/* Families count a shot under both of the feed's tagging schemes: an older
              PULL_HOOK_ON_BACK_FOOT is a pull, which a PULL filter alone misses. */}
          <Autocomplete
            multiple
            value={filters.shot_family || []}
            onChange={(e, value) => handleFilterChange('shot_family', value)}
            options={availableColumns?.shot_family_options || []}
            getOptionLabel={(option) => availableColumns?.shot_family_definitions?.[option]?.label || option}
            renderOption={(props, option) => (
              <li {...props} key={option}>
                <Box>
                  <Typography sx={{ fontSize: 14 }}>{availableColumns?.shot_family_definitions?.[option]?.label || option}</Typography>
                  <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                    {(availableColumns?.shot_family_definitions?.[option]?.shots || []).join(', ')}
                  </Typography>
                </Box>
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip variant="outlined" label={availableColumns?.shot_family_definitions?.[option]?.label || option} size="small" {...getTagProps({ index })} />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="Shot family" size="small" helperText="Every label for a shot, old and new tagging" />
            )}
          />
        </Grid>
        
        <Grid item xs={12} sm={4} md={3} style={sectionStyle('delivery')}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Shot Control</InputLabel>
              <Select
                value={filters.control ?? ''}
                onChange={(e) => handleFilterChange('control', e.target.value === '' ? null : parseInt(e.target.value))}
                label="Shot Control"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value={1}>Controlled</MenuItem>
                <MenuItem value={0}>Uncontrolled</MenuItem>
              </Select>
            </FormControl>
            <CoverageWarning coverage={availableColumns?.control_coverage} columnName="Control" />
          </Box>
        </Grid>
        
        <Grid item xs={12} sm={4} md={3} style={sectionStyle('delivery')}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Autocomplete
              multiple
              value={filters.wagon_zone || []}
              onChange={(e, value) => handleFilterChange('wagon_zone', value)}
              options={availableColumns?.wagon_zone_options || [0, 1, 2, 3, 4, 5, 6, 7, 8]}
              getOptionLabel={(option) => `Zone ${option}`}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="outlined" label={`Z${option}`} size="small" {...getTagProps({ index })} />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label="Wagon Zone" size="small" />
              )}
              sx={{ flexGrow: 1 }}
            />
            <InfoTooltip tooltip="Wagon wheel zones 0-8. Zone 0=No shot, 1-8=Direction of shot" />
          </Box>
        </Grid>
        
        {/* Row 8: Grouping - KEY FEATURE */}
        <Grid item xs={12}>
          <Box sx={{ 
            mt: isMobile ? 1.5 : 2, 
            p: isMobile ? 1.5 : 2, 
            borderRadius: 2, 
            border: '1.5px solid rgba(182,242,74,0.4)',
            backgroundColor: 'rgba(182,242,74,0.05)',
            position: 'relative'
          }}>
            {/* Key insight badge */}
            <Box sx={{ 
              display: isMobile ? 'none' : 'flex',
              position: 'absolute', 
              top: -12, 
              left: 16, 
              backgroundColor: qbColors.accent,
              color: qbColors.bg,
              px: 1.5,
              py: 0.25,
              borderRadius: 1,
              fontSize: '0.7rem',
              fontWeight: 'bold',
              fontFamily: qbFonts.mono,
              alignItems: 'center',
              gap: 0.5
            }}>
              🔑 KEY INSIGHT
            </Box>
            
            <Typography variant="subtitle2" sx={{ mb: 0.75, mt: isMobile ? 0 : 0.5, fontWeight: 'bold', color: qbColors.textHi, fontFamily: qbFonts.display, fontSize: 18 }}>
              {isMobile ? 'Group By (Insight Mode)' : 'Group by - this changes everything'}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mb: isMobile ? 1 : 1.5, color: qbColors.textLo }}>
              {isMobile
                ? 'Pick dimensions like batter, phase, competition, or match_outcome.'
                : 'Empty returns individual deliveries. Add dimensions like bowl_style, year, or phase to aggregate into chartable stats.'}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Autocomplete
                multiple
                value={groupBy}
                onChange={handleGroupByChange}
                options={availableColumns?.group_by_columns || []}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip 
                      variant="filled" 
                      label={option} 
                      size="small" 
                      sx={{ bgcolor: qbColors.accent, color: qbColors.bg, fontWeight: 700 }}
                      {...getTagProps({ index })} 
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Group By Columns" 
                    size="small"
                    placeholder="Select dimensions to analyze..."
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: qbColors.input,
                      }
                    }}
                  />
                )}
                sx={{ flexGrow: 1 }}
              />
              <InfoTooltip tooltip="Leave empty for individual deliveries. Group for aggregated stats with charts." />
            </Box>
          </Box>
        </Grid>
        
        {/* Summary Rows Toggle */}
        {groupBy && groupBy.length > 1 && (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={filters.show_summary_rows || false}
                    onChange={(e) => handleFilterChange('show_summary_rows', e.target.checked)}
                    size="small"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: qbColors.accent },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: qbColors.accent },
                    }}
                  />
                }
                label="Show Summary Rows"
              />
              <InfoTooltip tooltip="Add summary rows for each group level with % calculations" />
            </Box>
          </Grid>
        )}
        
        {/* Row 9: Result Filtering */}
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              label="Min Balls"
              type="number"
              value={filters.min_balls ?? ''}
              onChange={(e) => handleFilterChange('min_balls', e.target.value ? parseInt(e.target.value) : null)}
              inputProps={{ min: 0 }}
              size="small"
              fullWidth
            />
            <InfoTooltip tooltip="Minimum balls for grouped results (removes small samples)" />
          </Box>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Max Balls"
            type="number"
            value={filters.max_balls ?? ''}
            onChange={(e) => handleFilterChange('max_balls', e.target.value ? parseInt(e.target.value) : null)}
            inputProps={{ min: 0 }}
            size="small"
            fullWidth
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Min Runs"
            type="number"
            value={filters.min_runs ?? ''}
            onChange={(e) => handleFilterChange('min_runs', e.target.value ? parseInt(e.target.value) : null)}
            inputProps={{ min: 0 }}
            size="small"
            fullWidth
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Max Runs"
            type="number"
            value={filters.max_runs ?? ''}
            onChange={(e) => handleFilterChange('max_runs', e.target.value ? parseInt(e.target.value) : null)}
            inputProps={{ min: 0 }}
            size="small"
            fullWidth
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Min Wickets"
            type="number"
            value={filters.min_wickets ?? ''}
            onChange={(e) => handleFilterChange('min_wickets', e.target.value ? parseInt(e.target.value, 10) : null)}
            inputProps={{ min: 0 }}
            size="small"
            fullWidth
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Max Wickets"
            type="number"
            value={filters.max_wickets ?? ''}
            onChange={(e) => handleFilterChange('max_wickets', e.target.value ? parseInt(e.target.value, 10) : null)}
            inputProps={{ min: 0 }}
            size="small"
            fullWidth
          />
        </Grid>
        
        {/* Metric thresholds ("50+ average"): applied to grouped rows before sorting and the limit. */}
        <Grid item xs={12}>
          <MetricThresholds
            value={filters.having || []}
            onChange={(next) => handleFilterChange('having', next)}
          />
        </Grid>

        {/* Row 10: Query Settings */}
        <Grid item xs={12} sm={6}>
          <Box>
            <Typography variant="body2" gutterBottom sx={{ color: qbColors.textMed }}>Result Limit: {filters.limit}</Typography>
            <Slider
              value={filters.limit}
              onChange={(e, value) => handleFilterChange('limit', value)}
              min={100}
              max={10000}
              step={100}
              marks={[
                { value: 100, label: '100' },
                { value: 1000, label: '1K' },
                { value: 5000, label: '5K' },
                { value: 10000, label: '10K' }
              ]}
              valueLabelDisplay="auto"
              size="small"
            />
          </Box>
        </Grid>
        
        {filters.include_international && !internationalOnly && (
          <Grid item xs={12} sm={6}>
            <Box>
            <Typography variant="body2" gutterBottom sx={{ color: qbColors.textMed }}>Top Teams: {filters.top_teams ?? 'All'}</Typography>
              <Slider
                value={filters.top_teams ?? 20}
                onChange={(e, value) => handleFilterChange('top_teams', value)}
                min={5}
                max={20}
                step={1}
                marks={[
                  { value: 5, label: '5' },
                  { value: 10, label: '10' },
                  { value: 15, label: '15' },
                  { value: 20, label: '20' }
                ]}
                valueLabelDisplay="auto"
                size="small"
              />
            </Box>
          </Grid>
        )}
        
        {/* Data coverage info */}
        {availableColumns?.total_deliveries && (
          <Grid item xs={12}>
            <Typography variant="caption" sx={{ color: qbColors.textFaint, fontFamily: qbFonts.mono }}>
              Total deliveries in database: {availableColumns.total_deliveries.toLocaleString()}
            </Typography>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default QueryFilters;
